import { css, html, LitElement, type PropertyValues} from "lit";
import { customElement, property, state } from "lit/decorators.js";
import './obs-map.ts';
import './account-menu.ts';
import { contributorContext, userContext, type User } from "./identity.ts";
import { provide } from "@lit/context";
import { Temporal } from "temporal-polyfill";
import { repeat } from "lit/directives/repeat.js";
import { classMap } from "lit/directives/class-map.js";
import { sightingDraftsContext, type SightingDrafts } from "./sighting-drafts.ts";
import type { LayersChangeDetail, MapMoveDetail, ObsMap, SubjectSighting } from "./obs-map.ts";
import { GROUPS } from './symbology.ts';
import { occurrences2segments } from './segments.ts';
import { SEGMENT_CARD_MIN } from './segment-summary.ts';
import './segment-summary.ts';
import { LAYERS_PARAM, layersParam, parseLayersParam, type ReferenceLayer } from "./reference-layers.ts";
import type { CloneSightingEvent, EditSightingEvent } from "./obs-summary.ts";
import { fetchDayOccurrences, fetchStaticAnimalNames, findOccurrence, NotBuiltYet, pacificDay, watchManifest, withinExtent } from "./read-path.ts";
import { fetchMe, fetchOwnSightings, fetchPublicSighting, overlayOwn, ownOccurrence, signIn as apiSignIn, signOut as apiSignOut, type Me } from "./write-api.ts";
import { initSentry } from "./sentry.ts";
import { promptGoogleSignIn } from "./google-signin.ts";
import './error-toast.ts';
import type ErrorToast from './error-toast.ts';
import './feedback-form.ts';
import './terms-prompt.ts';
import type { TermsPrompt } from './terms-prompt.ts';
import { agreedToCurrent } from './terms.ts';
import { isNetworkFailure, reportError, type ErrorReport } from './report-error.ts';
import { v7 } from "uuid";
import { fromLonLat } from './map-view.ts';
import { DEFAULT_REGION_SLUG, type Extent, isExtent, observationToday, regionBySlug, type Region } from "./constants.ts";
import { ObsPanel } from "./obs-panel.ts";
import { createRef, ref } from "lit/directives/ref.js";
import type { Contributor, Occurrence } from "./types.ts";
import lockupUrl from "./assets/lockup-dark.svg?url";
import { renderSiteNav } from './site-nav.ts';
import './site-search.ts';
import './subject-view.ts';
import type { OccurrenceTarget, SubjectView } from './subject-view.ts';
import { parseSubject, readableSubject, sameSubject, SUBJECT_PARAM, subjectParam, type Subject } from './subject.ts';

/**
 * The day's list, in the order the sightings came (newest first): a travel segment of
 * SEGMENT_CARD_MIN reports or more as one card in the place of its first-listed report,
 * the map's own segments (salish-fwyt.5), and every other sighting as its own. Keyed by
 * a segment's oldest report, which stays its oldest as later reports join it.
 */
type DayItem = {key: string, at: number} & ({reports: Occurrence[], sighting?: never} | {sighting: Occurrence, reports?: never});

function dayList(sightings: Occurrence[]): DayItem[] {
  const position = new Map(sightings.map((s, i) => [s.id, i]));
  return occurrences2segments(sightings).flatMap((segment): DayItem[] => {
    const reports = segment.occurrences;
    return reports.length >= SEGMENT_CARD_MIN
      ? [{key: `segment-${reports[0]!.id}`, at: Math.min(...reports.map(r => position.get(r.id)!)), reports}]
      : reports.map(sighting => ({key: sighting.id, at: position.get(sighting.id)!, sighting}));
  }).sort((a, b) => a.at - b.at);
}

// Every animal and group the sidebar opens is a killer whale (the register's catalogue
// holds no other species), so its sightings take the killer whale's colour (decision 029).
const SUBJECT_COLOR = GROUPS.orca.color;

initSentry();

const viewInitiallySmall = window.innerWidth < 800;

const dateRE = /^(\d\d\d\d-\d\d-\d\d)$/;

function parseUrlParams(searchParams: URLSearchParams) {
  const dateParam = searchParams.get('d');
  const date = dateParam && dateRE.test(dateParam)
    ? dateParam
    : observationToday().toString();

  const x = parseFloat(searchParams.get('x') || '');
  const y = parseFloat(searchParams.get('y') || '');
  const z = parseFloat(searchParams.get('z') || '');

  const hasValidMapPosition = !isNaN(x) && !isNaN(y) && !isNaN(z);

  const occurrenceId = searchParams.get('o') || null;

  // An unknown or absent slug falls back to the default rather than erroring —
  // a stale link should still show the map, just not the region it asked for.
  const region = regionBySlug(searchParams.get('r'));

  const layers = parseLayersParam(searchParams.get(LAYERS_PARAM));

  // The animal or matriline the sidebar shows in place of the day's list (salish-7ds1.1).
  const subject = parseSubject(searchParams.get(SUBJECT_PARAM));

  return {
    date,
    layers,
    subject,
    occurrenceId,
    region,
    mapPosition: hasValidMapPosition
      ? { x, y, z }
      : {
          x: viewInitiallySmall ? -13732579 : -13880076,
          y: viewInitiallySmall ? 6095660 : 6211076,
          z: viewInitiallySmall ? 7 : 8
        }
  };
}

const initialParams = parseUrlParams(new URLSearchParams(document.location.search));
const hadDateParam = new URLSearchParams(document.location.search).has('d');
const rawRegionParam = new URLSearchParams(document.location.search).get('r');
const hadMapPosition = ['x', 'y', 'z'].every(k => new URLSearchParams(document.location.search).has(k));

@customElement('salish-sea')
export default class SalishSea extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      position: fixed;
      inset: 0;
      overflow: hidden;
    }
    a {
      text-decoration: none;
    }
    /* The site's nav (src/site-nav.ts) on the header's dark blue: BeeAtlas's header
       items, white and dimmed, the current page full strength over the lockup's teal. */
    nav.site-nav {
      align-items: center;
      align-self: stretch;
      display: flex;
      flex: 1;
      gap: 4px;
      margin-left: 1rem;
      min-width: 0;
    }
    nav.site-nav a {
      align-items: center;
      align-self: stretch;
      border-bottom: 2px solid transparent;
      box-sizing: border-box;
      color: white;
      display: inline-flex;
      font-size: var(--fs-body1);
      font-weight: 400;
      gap: 6px;
      justify-content: center;
      min-width: 44px;
      opacity: 0.6;
      padding: 0 10px;
    }
    /* A label centred in the header puts Work Sans's baseline 0.25em below the middle,
       measured; it drops the rest of the way to the wordmark's. The icon stays centred. */
    nav.site-nav .nav-label {
      position: relative;
      top: calc(var(--wordmark-drop) - 0.25em);
    }
    nav.site-nav a:hover {
      opacity: 0.9;
    }
    nav.site-nav a[aria-current="page"] {
      border-bottom-color: var(--color-chrome-accent);
      opacity: 1;
    }
    nav.site-nav site-search {
      align-self: stretch;
      color: white;
      margin-left: auto;
      --site-search-accent: var(--color-chrome-accent);
    }
    /* The account button and its menu (src/account-menu.ts), last in the row: the
       nav's icon button at full strength, underlined in the teal while its menu is open. */
    account-menu {
      align-self: stretch;
      color: white;
      flex-shrink: 0;
      --account-menu-accent: var(--color-chrome-accent);
    }
    /* A phone: the lockup's mark without its wordmark, and the nav's icons without
       their names, so the mark, three links, search and the account button share 360
       pixels. */
    @media (max-width: 40rem) {
      h1 img {
        height: 1.75rem;
        object-fit: cover;
        object-position: left center;
        width: calc(1.75rem * 600 / 386.57);
      }
      nav.site-nav {
        gap: 0;
        margin-left: 0.25rem;
      }
      nav.site-nav .nav-label {
        display: none;
      }
    }

    header {
      align-items: center;
      background-color: var(--color-chrome);
      box-sizing: border-box;
      color: white;
      display: flex;
      justify-content: space-between;
      /* No vertical padding: the nav's items fill the header's height, so the current
         page's underline is its bottom edge, as BeeAtlas's is. */
      min-height: 3rem;
      /* How far below the header's middle the lockup's wordmark sits on its baseline:
         the lockup is centred, 1.75rem tall, and the wordmark's baseline (the foot of its
         "l") is at 291.95 of its 386.57 units. Every text in the header sits there. */
      --wordmark-drop: calc(1.75rem * (291.95 / 386.57 - 0.5));
      padding: 0 0.5rem;
      width: 100%;
    }

    h1 {
      align-items: center;
      display: flex;
      font-size: var(--fs-h6);
      gap: 0.5rem;
      margin: 0;
    }
    h1 img {
      display: block;
      height: 1.75rem;
    }

    main {
      display: flex;
      /* The toast hangs from this corner, so it clears the header without
         anyone having to hardcode the header's height. */
      position: relative;
      flex-direction: row;
      flex-grow: 1;
      min-height: 0;
      overflow: hidden;
    }
    obs-panel {
      border-left: 1px solid var(--color-border);
      border-top: 0;
      padding: 0.5rem 0.5rem 5.5rem 0.5rem;
      width: 25rem;
    }
    /* The sidebar's view of one animal or matriline, in the panel's place (salish-7ds1.1). */
    subject-view {
      border-left: 1px solid var(--color-border);
      flex-shrink: 0;
      width: 25rem;
    }
    /* Each sets its own display, which would otherwise outrank the hidden attribute. */
    obs-panel[hidden], subject-view[hidden] {
      display: none;
    }

    /* COMPACT_LAYOUT in obs-panel.ts, which folds the month grid away in this layout. */
    @media (max-aspect-ratio: 1) {
      main {
        flex-direction: column;
      }
      obs-map {
        /* Not just shrink: until the panel renders it is short, and a growing
           map took the slack, then lost it a frame later. */
        flex-grow: 0;
        flex-shrink: 0;
        height: 50svh;
      }
      obs-panel, subject-view {
        border-left: 0;
        border-top: 1px solid var(--color-border);
        flex-grow: 1;
        min-height: 0;
        overflow: auto;
        width: 100%;
      }
    }

  `;

  @provide({context: sightingDraftsContext})
  sightingDrafts: SightingDrafts | undefined

  #isRestoringFromHistory = false
  #isFocusingOccurrence = false
  #mapMoveDebounceTimer: ReturnType<typeof setTimeout> | null = null

  @property({attribute: false})
  private focusedOccurrenceId: string | null = initialParams.occurrenceId;

  private mapRef = createRef<ObsMap>();
  private panelRef = createRef<ObsPanel>();
  private errorToastRef = createRef<ErrorToast>();
  private termsPromptRef = createRef<TermsPrompt>();

  @state()
  private lastOwnOccurrence: Occurrence | null = null;

  @provide({context: userContext})
  @state()
  protected user: User | undefined;

  @provide({context: contributorContext})
  @state()
  protected contributor: Contributor | undefined;

  #date: string = initialParams.date;
  @property({type: String, reflect: true})
  get date() { return this.#date }
  set date(d: string) {
    if (d === this.#date)
      return;
    this.#date = d;
    this.refetchOccurrences(d);
    if (!this.#isRestoringFromHistory) {
      if (this.#isFocusingOccurrence) {
        setQueryParams({d});
      } else {
        // A user-initiated day change clears any selected observation — it belongs to
        // another day — including its ?o= in the URL (single history entry).
        this.focusedOccurrenceId = null;
        setQueryParams({d}, {remove: ['o']});
      }
    }
  }

  #region: Region = initialParams.region;
  /**
   * The scope of every occurrence query, independent of the viewport. Panning
   * away from a region does not widen it — that is what makes this a filter and
   * not the "Go to" it grew out of.
   */
  get region() { return this.#region }
  set region(r: Region) {
    if (r.slug === this.#region.slug)
      return;
    this.#region = r;
    this.requestUpdate();
    this.refetchOccurrences(this.date);
    // The calendar invalidates its own counts when the new slug reaches it —
    // doing it from here would race the property propagation and cache the
    // region we just left. See date-calendar's willUpdate.
    if (!this.#isRestoringFromHistory) {
      if (r.slug === DEFAULT_REGION_SLUG)
        setQueryParams({}, {remove: ['r']});
      else
        setQueryParams({r: r.slug});
    }
  }

  @property({attribute: false})
  private sightings: Occurrence[] = []

  /**
   * The animal or matriline the sidebar shows in place of the day's list, or null for
   * the list (salish-7ds1.1). In the URL as `s=`, so a link opens it and Back leaves it.
   */
  @state()
  private subject: Subject | null = initialParams.subject;

  private subjectViewRef = createRef<SubjectView>();

  /** The link a subject was opened from, out of the day's list, which gets focus back when it closes. */
  #returnFocus: HTMLElement | null = null;
  /** Set when a subject was opened by a click or a key, so the view takes focus once it renders. */
  #focusView = false;
  /** Set while the sidebar's Back waits for the browser to step back, so a second press can't step again. */
  #closing = false;
  /**
   * Set when the visitor goes back from the subject, by either Back, so focus returns to
   * the link it was opened from. Closed any other way (a dot on the map, Edit, "Last
   * reported"), the page has put the visitor somewhere else, and focus stays put.
   */
  #restoreFocus = false;

  #openSubject = (evt: Event) => {
    const subject = (evt as CustomEvent<Subject>).detail;
    if (sameSubject(subject, this.subject)) return;
    if (!this.subject) {
      const origin = evt.composedPath()[0];
      this.#returnFocus = origin instanceof HTMLElement ? origin : null;
    }
    this.#focusView = true;
    // What the sidebar's Back will return to: the subject open now, by name, or the day.
    const backTo = this.subject ? this.subjectViewRef.value?.subjectTitle ?? null : null;
    this.subject = subject;
    // Marked as opened here, so the sidebar's Back can step back through history to
    // exactly where the visitor was, list or subject.
    setQueryParams({[SUBJECT_PARAM]: subjectParam(subject)}, {state: {subjectOpenedHere: true, backTo}});
  };

  /** The open subject's sightings, as its view last sent them; null until they arrive. */
  #subjectSightings: SubjectSighting[] | null = null;

  #onSubjectSightings = (evt: Event) => {
    this.#subjectSightings = (evt as CustomEvent<SubjectSighting[]>).detail;
    if (this.subject) this.mapRef.value?.setSubjectSightings(this.#subjectSightings, SUBJECT_COLOR);
  };

  /** What the map shows: an open subject's sightings in place of the day's (decision 096), or the day's. */
  #drawMap() {
    const map = this.mapRef.value;
    if (!map) return;
    if (this.subject) {
      map.setOccurrences([]);
      map.setSubjectSightings(this.#subjectSightings, SUBJECT_COLOR);
    } else {
      map.setSubjectSightings(null, SUBJECT_COLOR);
      map.setOccurrences(this.sightings);
    }
  }

  protected willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed);
    // Another subject's sightings, or none: the last one's go before its view sends these.
    if (changed.has('subject')) this.#subjectSightings = null;
  }

  protected updated(changed: PropertyValues): void {
    super.updated(changed);
    if (!changed.has('subject')) return;
    this.#drawMap();
    if (this.subject && this.#focusView) {
      // Opened by the visitor: focus moves into the view, to its Back button.
      this.#focusView = false;
      this.subjectViewRef.value?.updateComplete.then(() => this.subjectViewRef.value?.focusBack());
    } else if (!this.subject && changed.get('subject')) {
      // Gone back from: focus returns to the link it was opened from, if it's still there.
      const back = this.#restoreFocus ? this.#returnFocus : null;
      this.#returnFocus = null;
      this.#restoreFocus = false;
      if (back?.isConnected) back.focus();
    }
  }

  /** Close the subject without a history entry of its own, for an act that needs the day's list: editing, cloning. */
  #leaveSubject() {
    if (!this.subject) return;
    this.subject = null;
    // A fresh state: this entry no longer shows a subject, opened here or not.
    setQueryParams({}, {remove: [SUBJECT_PARAM], replace: true, state: {}});
  }

  #closeSubject = () => {
    if (this.#closing) return;
    this.#restoreFocus = true;
    const state = window.history.state as {subjectOpenedHere?: boolean, stepsBack?: number} | null;
    if (state?.subjectOpenedHere) {
      // Back to where the subject was opened, however many entries ago: deselecting a
      // sighting with the subject open adds one (setQueryParams counts them).
      this.#closing = true;
      window.history.go(-(state.stepsBack ?? 1));
    } else {
      // Opened from a link: there is nothing of ours to step back to, so the subject is
      // taken out of this entry, and the browser's Back still leaves the site.
      this.subject = null;
      setQueryParams({}, {remove: [SUBJECT_PARAM], replace: true, state: {}});
    }
  };

  // "Last reported" in a subject's view: the map moves to that day and selects the
  // sighting, which closes the subject. One history entry, so Back returns to it.
  // Only once the sighting is found: the URL and the screen must not disagree about the day.
  #goToOccurrence = (evt: Event) => {
    const {occurrenceId} = (evt as CustomEvent<OccurrenceTarget>).detail;
    this.hydrateFromOccurrenceId(occurrenceId)
      .then(found => {
        if (!found) {
          reportError(this, "That sighting is no longer on the map.", {capture: false});
          return;
        }
        this.subject = null;
        // The day the sighting was found on, which can differ from the link's for a
        // sighting edited since the last build: the URL says what the screen shows.
        setQueryParams({d: this.date, o: occurrenceId}, {remove: [SUBJECT_PARAM]});
        this.requestUpdate();
      })
      .catch(err => reportError(this, "Couldn't find that sighting.", {cause: err}));
  };

  /** The map's reference layers switched on (GH #453). Replaces the URL entry rather than adding one: it is a view setting, not a place to go Back to. */
  @state()
  private layers: ReadonlySet<ReferenceLayer> = initialParams.layers;

  #onLayersChange = (evt: Event) => {
    this.layers = (evt as CustomEvent<LayersChangeDetail>).detail;
    const value = layersParam(this.layers);
    if (value === null)
      setQueryParams({}, {remove: [LAYERS_PARAM], replace: true});
    else
      setQueryParams({[LAYERS_PARAM]: value}, {replace: true});
  };

  #handlePopState = () => {
    this.#closing = false;
    // The browser's Back or Forward: if it takes the subject away, that is going back from it.
    if (this.subject && !parseSubject(new URLSearchParams(window.location.search).get(SUBJECT_PARAM))) this.#restoreFocus = true;
    this.#isRestoringFromHistory = true;
    if (this.#mapMoveDebounceTimer) {
      clearTimeout(this.#mapMoveDebounceTimer);
      this.#mapMoveDebounceTimer = null;
    }
    try {
      const params = parseUrlParams(new URLSearchParams(window.location.search));
      this.region = params.region;
      this.date = params.date;
      this.layers = params.layers;
      this.subject = params.subject;
      this.focusedOccurrenceId = params.occurrenceId;
      this.mapRef.value?.setView(
        params.mapPosition.x,
        params.mapPosition.y,
        params.mapPosition.z,
        {skipEvent: true}
      );
    } finally {
      this.#isRestoringFromHistory = false;
    }
  };

  constructor() {
    super();
    // The write API's session is a cookie it set (decision 065): ask who it names.
    fetchMe()
      .then(me => this.#signedInAs(me))
      .catch(err => reportError(this, "Couldn't load your account. You may not be able to report a sighting.", {cause: err}));
    this.addEventListener('report-error', evt => {
      const {message, persist} = (evt as CustomEvent<ErrorReport>).detail;
      this.errorToastRef.value?.show(message, {persist});
    });
    this.addEventListener('log-in', this.doLogIn.bind(this));
    this.addEventListener('log-out', this.doLogOut.bind(this));
    this.addEventListener('focus-occurrence', evt => {
      const occurrence = (evt as CustomEvent<Occurrence | null>).detail;
      this.focusOccurrence(occurrence);
    });
    this.addEventListener('date-selected', (evt) => {
      const detail: unknown = evt instanceof CustomEvent ? evt.detail : undefined;
      if (typeof detail !== 'string')
        throw new Error(`date-selected carried ${typeof detail}, expected a YYYY-MM-DD date string`);
      this.date = detail;
    });
    this.addEventListener('go-to-extent', (evt) => {
      const extent = (evt as CustomEvent<Extent>).detail;
      if (!isExtent(extent))
        throw new Error(`Invalid extent: ${extent}`);
      this.mapRef.value!.zoomToExtent(extent);
    });
    this.addEventListener('region-selected', (evt) => {
      const slug = (evt as CustomEvent<string>).detail;
      const region = regionBySlug(slug);
      // Selecting a region still moves the map, as the Go-to bubbles did before
      // they became filters. Do this unconditionally — re-picking the current
      // region after panning away is a reasonable way to ask to go back.
      this.mapRef.value!.zoomToExtent([...region.zoomExtent]);
      this.region = region;
    });
    this.addEventListener('map-move', (evt) => {
      if (this.#isRestoringFromHistory)
        return;

      const {center: [x, y], zoom} = (evt as CustomEvent<MapMoveDetail>).detail;

      // Debounce map updates to avoid spamming history
      if (this.#mapMoveDebounceTimer)
        clearTimeout(this.#mapMoveDebounceTimer);

      this.#mapMoveDebounceTimer = setTimeout(() => {
        setQueryParams({x: x.toFixed(), y: y.toFixed(), z: zoom.toFixed()}, {replace: true});
        this.#mapMoveDebounceTimer = null;
      }, 500);
    });
    this.addEventListener('sighting-saved', (evt) => {
      const occurrence = (evt as CustomEvent<Occurrence>).detail;
      // A first sighting carries the contributor's agreement to the terms of use (#739).
      const agreed = (evt as CustomEvent<{agree_terms?: number}>).detail.agree_terms;
      if (agreed !== undefined && this.contributor) this.contributor = {...this.contributor, terms_version: agreed};
      this.focusOccurrence(occurrence);
      // focusOccurrence only triggers fetchOccurrences when the date changes.
      // Explicitly refresh so a newly saved sighting for the current date appears immediately.
      this.refetchOccurrences(this.date);
    });
    this.addEventListener('terms-agreed', (evt) => {
      this.contributor = (evt as CustomEvent<Me>).detail.contributor as Contributor;
    });
    this.addEventListener('sighting-deleted', (evt) => {
      const id = (evt as CustomEvent<string>).detail;
      // Drop the row now rather than waiting on the refetch behind it, so the
      // row's disappearance is a consequence of the click that asked for it.
      // The refetch reconciles the rest of the list.
      this.#listRevision++;
      this.sightings = this.sightings.filter(sighting => sighting.id !== id);
      if (!this.subject) this.mapRef.value?.setOccurrences(this.sightings);
      if (this.focusedOccurrenceId === id)
        this.focusOccurrence(null);
      this.refetchOccurrences(this.date);
    });
    this.addEventListener('clone-sighting', (evt) => {
      const sighting = (evt as CloneSightingEvent).detail;
      // The form opens in the day's list, which the subject's view covers.
      this.#leaveSubject();
      const clone = {...sighting, id: v7()};
      this.panelRef.value!.editObservation(clone, {copy: true})
        .catch(err => reportError(this, "Couldn't open a copy of that sighting. Please try again.", {cause: err}));
    });
    this.addEventListener('edit-observation', (evt) => {
      const sighting = (evt as EditSightingEvent).detail;
      this.#leaveSubject();
      this.panelRef.value!.editObservation(sighting)
        .catch(err => reportError(this, "Couldn't open that sighting for editing. Please try again.", {cause: err}));
    });
  }

  /** Stops the read-path manifest watch. */
  #stopManifestWatch: (() => void) | undefined;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('popstate', this.#handlePopState);
    // The day comes from the read-path files, which change when a build lands; the
    // manifest says when one has (decision 056). A tab's own saves refetch on their
    // own events, and everyone else's arrive with the build the manifest announces.
    this.#stopManifestWatch = watchManifest(async () => {
      this.panelRef.value?.revalidateCalendar();
      // A failed load is retried on the next poll rather than waiting for the
      // next build to come along.
      return this.fetchOccurrences(this.date).catch(err => {
        reportError(this, "Couldn't refresh sightings. The list may be out of date.", {cause: err, persist: true});
        return false;
      });
    });
    // Reflect the resolved date in the URL so a link shared while viewing the default
    // (today) is a permalink to that day, the way map coordinates already are. replaceState
    // adds no history entry; skip when an occurrence permalink (?o=) already pins context.
    if (!hadDateParam && !initialParams.occurrenceId)
      setQueryParams({d: this.#date}, {replace: true});
    // Normalise the region the same way. A slug we don't recognise resolved to
    // the default above, so leaving it in the URL would keep advertising a
    // region that isn't the one on screen.
    if (rawRegionParam !== null && rawRegionParam !== this.#region.slug) {
      if (this.#region.slug === DEFAULT_REGION_SLUG)
        setQueryParams({}, {replace: true, remove: ['r']});
      else
        setQueryParams({r: this.#region.slug}, {replace: true});
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#handlePopState);
    if (this.#mapMoveDebounceTimer) {
      clearTimeout(this.#mapMoveDebounceTimer);
    }
    this.#stopManifestWatch?.();
    this.#stopManifestWatch = undefined;
  }

  protected render(): unknown {
    const {x: initialX, y: initialY, z: initialZ} = initialParams.mapPosition;

    return html`
      <header>
        <h1><img src=${lockupUrl} alt="SalishSea.io"></h1>
        ${renderSiteNav('map')}
        <account-menu .lastOwnOccurrence=${this.lastOwnOccurrence}></account-menu>
      </header>
      <main @open-subject=${this.#openSubject} @close-subject=${this.#closeSubject} @go-to-occurrence=${this.#goToOccurrence} @subject-sightings=${this.#onSubjectSightings}>
        <obs-map ${ref(this.mapRef)} centerX=${initialX} centerY=${initialY} zoom=${initialZ} focusedOccurrenceId=${this.focusedOccurrenceId} .maskExtent=${this.region.extent} .visibleLayers=${this.layers} @layers-change=${this.#onLayersChange}></obs-map>
        <obs-panel ${ref(this.panelRef)} ?hidden=${!!this.subject} date=${this.date} regionSlug=${this.region.slug} .lastOwnOccurrence=${this.lastOwnOccurrence}>
          ${repeat(dayList(this.sightings), item => item.key, item => {
            if (item.reports)
              return html`<segment-summary .sightings=${item.reports} .focusedId=${this.focusedOccurrenceId}></segment-summary>`;
            const id = item.sighting.id;
            const classes = {focused: id === this.focusedOccurrenceId};
            return html`
              <obs-summary class=${classMap(classes)} id=${`summary-${id}`} ?focused=${classes.focused} .sighting=${item.sighting}></obs-summary>
            `;
          })}
        </obs-panel>
        <subject-view ${ref(this.subjectViewRef)} ?hidden=${!this.subject} .subject=${this.subject}
          back-label=${(window.history.state as {backTo?: string | null} | null)?.backTo
            ?? Temporal.PlainDate.from(this.date).toLocaleString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})}></subject-view>
        <error-toast ${ref(this.errorToastRef)}></error-toast>
        <feedback-form></feedback-form>
        <terms-prompt ${ref(this.termsPromptRef)}></terms-prompt>
      </main>
    `;
  }

  doLogIn() {
    promptGoogleSignIn((token, nonce) => {
      this.receiveIdToken(token, nonce).catch(err =>
        reportError(this, "Couldn't sign you in with Google. Please try again.", {cause: err}));
    }).catch(err => reportError(this, "Couldn't reach Google to sign in. An ad blocker may be blocking it.", {cause: err}));
  }

  /** The write API's answer to who is signed in, applied (decision 065). */
  #signedInAs(me: Me | null) {
    this.user = me ? {id: me.user_id} : undefined;
    // Before the refetch, which overlays this contributor's own sightings.
    const contributor = me ? me.contributor as Contributor : undefined;
    this.contributor = contributor;
    // Where the list comes from changes with who is signed in.
    this.#listRevision++;
    this.refetchOccurrences(this.date);
    if (!contributor) {
      this.lastOwnOccurrence = null;
      return;
    }
    const revision = this.#listRevision;
    const now = new Date();
    fetchOwnSightings(new Date(now.getTime() - 399 * 24 * 60 * 60_000), new Date(now.getTime() + 24 * 60 * 60_000))
      .then(async own => {
        const last = own[0];
        if (!last) return null;
        return ownOccurrence(last, contributor, await fetchStaticAnimalNames([last.entity_id]).catch(() => undefined));
      })
      // A sign-out or another sign-in since makes this answer someone else's.
      .then(occurrence => { if (revision === this.#listRevision) this.lastOwnOccurrence = occurrence; })
      .catch(err => reportError(this, "Couldn't find your last sighting.", {cause: err}));
  }

  async doLogOut() {
    try {
      await apiSignOut();
      this.#signedInAs(null);
    } catch (err) {
      reportError(this, "Couldn't sign you out. Please try again.", {cause: err});
    }
  }

  public async receiveIdToken(token: string, nonce: string) {
    try {
      const me = await apiSignIn(token, nonce);
      this.#signedInAs(me);
      // Asked once, as they sign in, if they haven't agreed to the terms of use (#739).
      if (!agreedToCurrent(me.contributor.terms_version)) this.termsPromptRef.value?.open();
    } catch (err) {
      reportError(this, "Couldn't sign you in with Google. Please try again.", {cause: err});
    }
  }

  protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
    this.sightingDrafts = this.mapRef.value!.drafts;
    // Frame the active region whenever the URL does not say otherwise —
    // including the default one, with no ?r= at all.
    //
    // The viewport and the region are the same statement about what you are
    // looking at, so they should not be able to disagree. They did: the old
    // hardcoded default centre/zoom was tuned long before regions existed and
    // is wider than the Salish Sea box, which nothing revealed until the mask
    // started drawing the difference. On a phone (default zoom 7, wider still)
    // that left roughly half the map shaded on first load, which reads as
    // "zoomed out and mostly disabled" rather than "here is the Salish Sea".
    //
    // An explicit x/y/z still wins, as does ?o=, which pins the map to an
    // occurrence.
    if (!hadMapPosition && !initialParams.occurrenceId)
      this.mapRef.value!.frameExtentWhenReady([...this.#region.zoomExtent]);
    if (initialParams.occurrenceId) {
      // Lit doesn't await firstUpdated's promise, so a rejection here is an
      // unhandled one. The link named a sighting; failing to reach it is worth
      // saying, because the map otherwise just sits on the default view.
      await this.hydrateFromOccurrenceId(initialParams.occurrenceId)
        .catch(err => reportError(this, "Couldn't open the sighting this link points to.", {cause: err}));
    }
  }

  /**
   * An occurrence named by `?o=` that the active region excludes.
   *
   * A permalink has to keep working whatever region the recipient lands in, so
   * this one is merged back into the region-filtered results — otherwise the
   * map centres on a point it has not drawn and the sidebar has nothing to
   * select. It stays outside the mask's clear window, which is the honest
   * picture: this sighting is real, and it is outside what you are looking at.
   *
   * Guarded on {@link focusedOccurrenceId} rather than cleared by hand, so it
   * stops applying the moment focus moves or the day changes.
   */
  #permalinkOccurrence: Occurrence | null = null;

  receiveOccurrences(occurrences: Occurrence[], forDate: string, forRegion: string) {
    // Both guards matter, and for different reasons. The date catches a day
    // change; the region catches a region change, which re-queries the SAME
    // date — so without it a slow in-flight request for the region you just
    // left can land last and repaint the map with out-of-region sightings that
    // the mask then shades over.
    if (forDate !== this.date || forRegion !== this.#region.slug)
      return;

    const pinned = this.#permalinkOccurrence;
    const merged = pinned
      && pinned.id === this.focusedOccurrenceId
      && dateFromObservedAt(pinned.observed_at) === forDate
      && !occurrences.some(o => o.id === pinned.id)
      ? [...occurrences, pinned].sort((a, b) => b.observed_at_ms - a.observed_at_ms)
      : occurrences;

    this.sightings = merged;
    // While a subject is open the map shows its sightings; the day's wait for it to close.
    if (!this.subject) this.mapRef.value!.setOccurrences(merged);
  }

  focusOccurrence(occurrence: Occurrence | null) {
    this.focusedOccurrenceId = occurrence?.id || null;
    // A sighting is shown in the day's list, so focusing one (a dot on the map, your last
    // sighting from the account menu) closes the subject the sidebar shows; one history entry.
    const leaving = !!occurrence && !!this.subject;
    if (leaving) this.subject = null;
    if (occurrence) {
      // Focusing may change the date; flag it so the date setter doesn't treat this as a
      // user day-change and clear the focus we just set.
      this.#isFocusingOccurrence = true;
      try {
        this.date = Temporal.Instant.from(occurrence.observed_at).toZonedDateTimeISO('America/Los_Angeles').toPlainDate().toString();
      } finally {
        this.#isFocusingOccurrence = false;
      }
    }

    if (!this.#isRestoringFromHistory) {
      if (this.focusedOccurrenceId) {
        setQueryParams({o: this.focusedOccurrenceId}, leaving ? {remove: [SUBJECT_PARAM]} : {});
      } else {
        removeQueryParam('o');
      }
    }
  }

  /**
   * Fire-and-forget {@link fetchOccurrences}, for the callers that have no
   * `await` to hang a failure from — property setters and event listeners.
   *
   * A failed *query* already reports itself from inside `fetchOccurrences`,
   * with the staleness guard that decides whether it is still worth saying.
   * This catches what is left: a rejection from the surrounding work, which
   * would otherwise be an unhandled promise rejection and, since these callers
   * are exactly the ones that repaint the list, a sighting list silently
   * frozen on the previous day.
   */
  private refetchOccurrences(date: string): Promise<void> {
    return this.fetchOccurrences(date)
      .then(() => {}, err => reportError(this, "Couldn't refresh sightings. The list may be out of date.", {cause: err, persist: true}));
  }

  /**
   * Bumped whenever the list is edited locally ahead of the server — a confirmed
   * delete — or its source changes, on signing in or out. Date and region are
   * not enough to date a response: a request issued *before* the delete asks for
   * the same day and the same region, so both guards pass and it repaints the
   * row we just removed. It simply predates the edit, and this is what says so.
   */
  #listRevision = 0;

  /**
   * Resolves false when the load failed (and was reported), so a caller that can
   * retry — the read-path manifest watch — knows to. A response superseded by a
   * newer request still counts as done: the newer one covers it.
   */
  async fetchOccurrences(date: string): Promise<boolean> {
    // Captured up front: `this.#region` can change while this is in flight, and
    // the response has to be judged against the region that asked for it. Same
    // for the revision — see #listRevision.
    const region = this.#region;
    const revision = this.#listRevision;
    const startOfDay = Temporal.PlainDate.from(date).toZonedDateTime({timeZone: 'America/Los_Angeles', plainTime: '00:00:00'});
    const endOfDay = startOfDay.add({days: 1});
    type Row = Omit<Occurrence, 'observed_at_ms'>;
    let data;
    try {
      // The day's file (decision 056), the region filter applied to its rows in
      // read-path.ts. The files trail the store by up to a build, and a contributor must
      // see a sighting they just saved (decision 055), so a signed-in tab overlays its
      // own sightings from the write API (decision 065). Every sign-in or out refetches.
      if (!this.user) {
        data = await fetchDayOccurrences<Row>(date, region.extent);
      } else {
        // Between Pacific midnight and the first build of the new day, the day
        // has no file yet. A signed-out tab is told so (below, as an error); a
        // signed-in one still has its own sightings live, and a contributor who
        // just saved one must see it (decision 055) — so the file side reads as
        // empty for now, and a notice says the rest is on its way. Not a
        // failure of ours, so Sentry doesn't hear it (salish-xv35.22).
        let notBuiltYet = false;
        const fileSide = fetchDayOccurrences<Row>(date, region.extent).catch((err: unknown) => {
          if (!(err instanceof NotBuiltYet)) throw err;
          notBuiltYet = true;
          return [] as Row[];
        });
        // A contributor's own sightings as saved, over the file's copies of them;
        // everyone else's come with the build.
        const contributor = this.contributor;
        // Their own failing to load leaves the published day standing, theirs included
        // as of the last build, rather than failing the list.
        let ownFailure: unknown = null;
        const [file, own] = await Promise.all([
          fileSide,
          contributor
            ? fetchOwnSightings(new Date(startOfDay.epochMilliseconds), new Date(endOfDay.epochMilliseconds))
              .catch((err: unknown) => { ownFailure = err; return null; })
            : [],
        ]);
        if (ownFailure && date === this.date && region.slug === this.#region.slug && revision === this.#listRevision)
          reportError(this, "Couldn't load your latest sightings; showing them as last published.", {cause: ownFailure});
        const names = own?.length ? await fetchStaticAnimalNames(own.map(o => o.entity_id)).catch(() => undefined) : undefined;
        data = contributor && own
          ? overlayOwn(file, withinExtent(own.map(o => ownOccurrence(o, contributor, names)) as unknown as Row[], region.extent), contributor.id)
          : file;
        if (notBuiltYet && date === this.date && region.slug === this.#region.slug && revision === this.#listRevision)
          reportError(this, "Today's sightings from other sources arrive with the next update; yours are shown.", {capture: false});
      }
    } catch (err) {
      // Same staleness guard as receiveOccurrences, for the same reasons: a
      // slow request for the day or region you just left must not speak for
      // the one you are looking at now, and here it would claim the current,
      // complete list is incomplete.
      // Persist: an empty list is indistinguishable from a quiet day, so a
      // message that times out would leave the map lying about the water.
      // A dropped connection is reported under one fingerprint, whichever code
      // asked for the day: otherwise every change to the callers starts a new
      // Sentry issue for it (SALISHSEA-IO-3Q, -3R, -4H), and the one number that
      // tells an outage from ordinary lost signal is split across them.
      if (date === this.date && region.slug === this.#region.slug && revision === this.#listRevision)
        reportError(this, "Couldn't load sightings. The list may be incomplete.", {
          cause: err,
          persist: true,
          fingerprint: isNetworkFailure(err) ? ['day-file-network-failure'] : undefined,
        });
      return false;
    }

    const occurrences = data.map(record => ({
      observed_at_ms: Date.parse(record.observed_at),
      ...record,
    }));

    if (revision !== this.#listRevision)
      return true;
    this.receiveOccurrences(occurrences as Occurrence[], date, region.slug);
    return true;
  }

  /**
   * Open the app on the sighting a `?o=` link names: fetch it, move the day and
   * the map to it, and focus it. It runs before the first occurrence fetch so
   * that fetch is the first response that has to carry the sighting — the
   * active region may well exclude it, which is what {@link #permalinkOccurrence}
   * is for.
   *
   * Rejects when the lookup fails; resolves false, having done nothing, when the id
   * names no sighting we have, and true once it has moved to the sighting.
   */
  private async hydrateFromOccurrenceId(id: string): Promise<boolean> {
    // From the read-path files: the id index says which day, and the day's file has
    // the sighting (decision 056). An error throws; an id we don't have resolves to null.
    let occurrence = await findOccurrence<Occurrence>(id);
    // A native sighting no file holds yet — saved since the last build, and shared at
    // once — is in the store, which the API answers for anyone (salish-9uu.5). Only a
    // bare uuid is asked about: an upstream id carries its source (`maplify:…`) and
    // comes with the build or not at all.
    if (!occurrence && !id.includes(':'))
      occurrence = await fetchPublicSighting(id);
    if (!occurrence) return false; // not found — silent fallback per decisions

    const date = dateFromObservedAt(occurrence.observed_at);
    // Set before fetching: the fetch's own response is the first one that has
    // to carry this occurrence, since the region may well exclude it.
    this.#permalinkOccurrence = {
      ...occurrence,
      observed_at_ms: Date.parse(occurrence.observed_at),
    };
    // Bypass the date setter to avoid writing ?d= to history
    this.#date = date;
    await this.fetchOccurrences(date);

    // Center map on occurrence location
    const {lon, lat} = occurrence.location as {lon: number; lat: number};
    const coord = fromLonLat([lon, lat]);
    this.mapRef.value!.setView(coord[0]!, coord[1]!, 12, {skipEvent: true});
    this.focusedOccurrenceId = id;
    return true;
  }
}

export function dateFromObservedAt(observedAt: string): string {
  return pacificDay(observedAt);
}

/** Write the page's URL; exported for its tests. */
export function setQueryParams(params: {[k: string]: string}, options: {replace?: boolean, remove?: string[], state?: object} = {}) {
    const url = new URL(window.location.href);
    for (const [k, v] of Object.entries(params)) {
      url.searchParams.set(k, v);
    }
    for (const k of options.remove ?? []) {
      url.searchParams.delete(k);
    }
    // A new entry while a subject opened here stays open (deselecting a sighting, say)
    // carries the subject's state on, one step further from where it was opened, so the
    // sidebar's Back still lands there.
    const current = window.history.state as {subjectOpenedHere?: boolean, stepsBack?: number} | null;
    const pushState = options.state
      ?? (current?.subjectOpenedHere && url.searchParams.has(SUBJECT_PARAM)
        ? {...current, stepsBack: (current.stepsBack ?? 1) + 1}
        : {});
    if (options.replace) {
      // Replacing keeps the entry's own state unless given another: a map move rewrites
      // the URL, and must not forget that the subject on screen was opened here.
      window.history.replaceState(options.state ?? window.history.state ?? {}, '', readableSubject(url));
    } else {
      window.history.pushState(pushState, '', readableSubject(url));
    }
}

function removeQueryParam(key: string) {
    setQueryParams({}, {remove: [key]});
}


declare global {
  interface HTMLElementTagNameMap {
    "salish-sea": SalishSea;
  }
}
